import { useCallback } from "react";

interface UseBalanceSeekOptions {
    /** Current input value as a string (matches AmountInput's `value` prop). */
    amount: string;
    /** Maximum balance available, as a number. */
    balance: number;
    /** Decimals to truncate to when computing seeked values. */
    decimals: number;
    /** Setter / change handler — called with the new sanitized string. */
    onChange: (next: string) => void;
    /**
     * Optional override for the 100% case. When provided, called instead of
     * `onChange(balance.toFixed(decimals))`. Use when "max" needs side effects
     * beyond setting the input (e.g., recording the max click for analytics or
     * preparing a quote).
     */
    onMax?: () => void;
}

interface UseBalanceSeekReturn {
    /** SeekBar fill percentage (0-100), clamped. */
    pct: number;
    /** Handler for AmountInput's `onSeek` prop. */
    onSeek: (nearest: number) => void;
}

/**
 * Wires an AmountInput's percentage seekbar to a balance-driven value.
 * Computes `pct` from the current amount / balance, and exposes an `onSeek`
 * that:
 *
 * - 100% — calls `onMax()` if provided, else `onChange(balance.toFixed(decimals))`
 * - 0%  — clears the input via `onChange("")`
 * - other — `onChange(Math.floor((balance * pct / 100) * 10^decimals) / 10^decimals)`
 *
 * Truncates (not rounds) so the resulting amount never exceeds the user's
 * balance by sub-token rounding.
 */
export function useBalanceSeek({
    amount,
    balance,
    decimals,
    onChange,
    onMax
}: UseBalanceSeekOptions): UseBalanceSeekReturn {
    const parsed = Number(amount) || 0;
    const pct = balance > 0 ? Math.min((parsed / balance) * 100, 100) : 0;

    const onSeek = useCallback(
        (nearest: number): void => {
            if (nearest === 100) {
                if (onMax) onMax();
                else {
                    const factor = Math.pow(10, decimals);
                    const truncated = Math.floor(balance * factor) / factor;
                    onChange(truncated.toFixed(decimals));
                }
                return;
            }
            if (nearest === 0) {
                onChange("");
                return;
            }
            const factor = Math.pow(10, decimals);
            const truncated = Math.floor(((balance * nearest) / 100) * factor) / factor;
            onChange(truncated.toFixed(decimals));
        },
        [balance, decimals, onChange, onMax]
    );

    return { pct, onSeek };
}
